import { DateTime } from 'luxon';

/**
 * Formats UTC timestamp to field's local timezone
 * @param {string|Date} utcDate - UTC date from database
 * @param {string} timezone - Field's timezone (e.g., 'America/New_York')
 * @param {Object} options - Formatting options
 * @returns {Object} Formatted date and time
 */
export const formatToFieldTimezone = (utcDate, timezone = 'America/New_York', options = {}) => {
  if (!utcDate) {
    return {
      date: 'Invalid Date',
      time: 'Invalid Time',
      dateTime: 'Invalid DateTime',
      iso: null,
      localDate: null,
      localTime: null
    };
  }

  const dt = typeof utcDate === 'string'
    ? DateTime.fromISO(utcDate, { zone: 'utc' }).setZone(timezone)
    : DateTime.fromJSDate(utcDate).setZone(timezone);

  if (!dt.isValid) {
    return {
      date: 'Invalid DateTime',
      time: 'Invalid Time',
      dateTime: 'Invalid DateTime',
      iso: null,
      localDate: null,
      localTime: null
    };
  }
  
  return {
    date: dt.toLocaleString(DateTime.DATE_FULL, options),
    time: dt.toLocaleString(DateTime.TIME_SIMPLE, options),
    dateTime: dt.toLocaleString(DateTime.DATETIME_FULL, options),
    iso: dt.toISO(),
    localDate: dt.toFormat('yyyy-MM-dd'),
    localTime: dt.toFormat('HH:mm')
  };
};

/**
 * Parses a DB datetime value (ISO string with 'Z', a naive 'YYYY-MM-DD HH:mm:ss'
 * string, or a Date) as a Luxon DateTime in UTC. Returns null if unparseable.
 * @param {string|Date|null|undefined} value
 * @returns {DateTime|null}
 */
export const parseUTCDate = (value) => {
  if (!value) return null;
  const s = typeof value === 'string' ? value : new Date(value).toISOString();
  const dt = DateTime.fromISO(s.includes('T') ? s : s.replace(' ', 'T'), { zone: 'utc' });
  return dt.isValid ? dt : null;
};

/**
 * Converts local date/time strings to UTC for API calls
 * @param {string} localDate - 'YYYY-MM-DD' format
 * @param {string} localTime - 'HH:mm' or 'HH:mm:ss' format  
 * @param {string} timezone - Field's timezone
 * @returns {Date} UTC Date object
 */
export const convertLocalToUTC = (localDate, localTime, timezone = 'America/New_York') => {
  // Handle time strings with or without seconds (HH:mm or HH:mm:ss)
  const timeFormat = localTime.split(':').length === 3 ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd HH:mm';
  const dt = DateTime.fromFormat(`${localDate} ${localTime}`, timeFormat, { zone: timezone });
  if (!dt.isValid) {
    throw new Error(`Invalid date/time: ${localDate} ${localTime} in timezone ${timezone}`);
  }
  return dt.toUTC().toJSDate();
};

/**
 * Formats time string for display (24hr to 12hr)
 * @param {string} timeStr - 'HH:mm' format
 * @returns {string} Formatted time like '2:30 PM'
 */
export const formatTimeDisplay = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  // Schedule end times store midnight as '24:00:00' 
  // (see normalizeScheduleTimeRange in Node/utils/bookingHelpers.js)
  // satisfies endTime > startTime; display it as 12:00 AM.
  if (hours === 24) return `12:${minutes.toString().padStart(2, '0')} AM`;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

/**
 * Gets current date in field's timezone
 * @param {string} timezone - Field's timezone
 * @returns {string} Current date in 'YYYY-MM-DD' format
 */
export const getFieldCurrentDate = (timezone = 'America/New_York') => {
  return DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd');
};

/**
 * Checks if a date is in the past relative to field's timezone
 * @param {string} localDate - 'YYYY-MM-DD' format
 * @param {string} timezone - Field's timezone
 * @returns {boolean} True if date is in the past
 */
export const isDateInPast = (localDate, timezone = 'America/New_York') => {
  const fieldDate = DateTime.fromFormat(localDate, 'yyyy-MM-dd', { zone: timezone });
  const now = DateTime.now().setZone(timezone);
  return fieldDate < now.startOf('day');
};

/**
 * Validates booking time against field's current time
 * @param {string} localDate - 'YYYY-MM-DD' format
 * @param {string} localTime - 'HH:mm' format
 * @param {string} timezone - Field's timezone
 * @param {number} minLeadTime - Minimum lead time in hours
 * @returns {Object} Validation result
 */
export const validateBookingTime = (localDate, localTime, timezone = 'America/New_York', minLeadTime = 0) => {
  const bookingDateTime = DateTime.fromFormat(`${localDate} ${localTime}`, 'yyyy-MM-dd HH:mm', { zone: timezone });
  const now = DateTime.now().setZone(timezone);
  
  const minBookingTime = now.plus({ hours: minLeadTime });
  
  return {
    isValid: bookingDateTime.isValid && bookingDateTime >= minBookingTime,
    isPast: bookingDateTime.isValid && bookingDateTime < now,
    message: !bookingDateTime.isValid 
      ? `Invalid date/time format: ${localDate} ${localTime}`
      : bookingDateTime < minBookingTime 
        ? `Bookings must be made at least ${minLeadTime} hour${minLeadTime !== 1 ? 's' : ''} in advance`
        : null
  };
};
